// PHASE_4 / B7-frontend — display-only time formatting for the calendar.
//
// TIMEZONE CORRECTNESS (the load-bearing rule): the API carries a UTC INSTANT
// (`starts_at`/`ends_at` ISO-8601) PLUS a separate IANA `timezone` string the
// event was authored in. We format the instant IN THAT ZONE — we do NOT shift
// the instant ourselves (no manual offset math) and we do NOT re-interpret the
// stored UTC as local time. `Intl.DateTimeFormat({ timeZone })` does the single,
// correct conversion: instant + zone → wall-clock string. Calling it twice or
// adding an offset would double-convert; we never do.
//
// `locale` is the active i18n language (tr-TR primary) so weekday/month names +
// 24h/12h conventions localize. This module formats ONLY — it never parses a
// figure, carries money, or mutates a time.

/** A bad/empty ISO string falls back to the raw input rather than "Invalid Date". */
function isValidIso(iso: string): boolean {
  return iso !== "" && !Number.isNaN(new Date(iso).getTime());
}

/**
 * Format an ISO-8601 UTC instant as a wall-clock date+time IN the given IANA
 * timezone, localized to `locale`. The zone is the event's authored zone — the
 * conversion happens exactly once inside Intl. Returns the raw ISO on a bad
 * input or an unknown zone (Intl throws RangeError on an invalid timeZone).
 */
export function formatEventTime(iso: string, timezone: string, locale: string): string {
  if (!isValidIso(iso)) return iso;
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: timezone,
    }).format(new Date(iso));
  } catch {
    // Unknown/invalid zone — format in UTC so we still show a sensible instant
    // rather than crashing the row.
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "UTC",
    }).format(new Date(iso));
  }
}

/**
 * Format an ISO instant as time-only (HH:mm) in the given zone + locale — used
 * for the availability slot list where the date is shown once per group.
 */
export function formatSlotTime(iso: string, timezone: string, locale: string): string {
  if (!isValidIso(iso)) return iso;
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "short",
      timeStyle: "short",
      timeZone: timezone,
    }).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "short",
      timeStyle: "short",
      timeZone: "UTC",
    }).format(new Date(iso));
  }
}

/** The browser's IANA timezone, defaulted to UTC — the create-event picker's default zone. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Convert a native `datetime-local` value (`YYYY-MM-DDTHH:mm`, no zone) into an
 * ISO-8601 string WITH offset, interpreting the wall-clock as LOCAL browser time.
 * The backend requires `datetime({ offset: true })`; `new Date(local)` parses a
 * zoneless datetime-local as local time, and `toISOString()` stamps the UTC
 * offset (Z). Returns null when the input is empty/unparseable so the caller can
 * surface a validation note rather than POST garbage.
 */
export function localInputToIso(value: string): string | null {
  if (value === "") return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** Count attendees defensively — the wire `attendees` is JSONB (best-effort array). */
export function attendeeCount(attendees: unknown): number {
  return Array.isArray(attendees) ? attendees.length : 0;
}

/**
 * Format an ISO instant (typically an rrule UNTIL bound, via
 * recurrence.ts's untilToIso) as a date, localized to `locale`. UNTIL is
 * stored as a UTC instant (RFC 5545 §3.3.10) but BOTH the picker (
 * recurrence.ts's localDateTimeToUntilUtc) and the server (rrule-expansion.ts's
 * zonedWallClockToFloatingUtc/floatingToRealUtc) treat that instant as a
 * WALL-CLOCK time in the window's own declared IANA `timezone` — UTC is a
 * storage encoding, not the reference frame either side actually reasons in.
 * Formatting in UTC here would show a date that can differ from what the
 * user typed AND from what the server enforces (SEM-2065 Phase B review,
 * HIGH #1); formatting in the window's own zone round-trips correctly to
 * both. Returns the raw ISO on a bad input or an unknown zone (defensive —
 * mirrors formatEventTime/formatSlotTime above).
 */
export function formatUntilDate(iso: string, timezone: string, locale: string): string {
  if (!isValidIso(iso)) return iso;
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeZone: timezone }).format(
      new Date(iso),
    );
  } catch {
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeZone: "UTC" }).format(
      new Date(iso),
    );
  }
}
